import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Warning } from "@phosphor-icons/react";

/**
 * Global Bestätigungs-Dialog (Phase 21α).
 *
 * Ersetzt `await window._domivusConfirm(...)` durch ein schön gestyltes shadcn-Dialog.
 * Verwendung:
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: "Dokument löschen?",
 *     description: "Diese Aktion kann nicht rückgängig gemacht werden.",
 *     confirmLabel: "Endgültig löschen",
 *     danger: true,
 *   });
 *   if (!ok) return;
 */

const ConfirmContext = createContext(null);

const DEFAULT_OPTS = {
  title: "Bist du sicher?",
  description: "",
  confirmLabel: "Bestätigen",
  cancelLabel: "Abbrechen",
  danger: false,
};

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, opts: DEFAULT_OPTS });
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ open: true, opts: { ...DEFAULT_OPTS, ...opts } });
    });
  }, []);

  const close = (result) => {
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
    setState((s) => ({ ...s, open: false }));
  };

  // iter160k — Globaler Zugriff für Legacy-Code (statt window.confirm).
  // Alle noch nicht refactorten `await window._domivusConfirm(x)` wurden per Bulk auf
  // `await window._domivusConfirm(x)` umgestellt. Diese Funktion nutzt das
  // gleiche Modal wie der `useConfirm`-Hook.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window._domivusConfirm = (msgOrOpts) => {
      if (typeof msgOrOpts === "string") {
        return confirm({ message: msgOrOpts, danger: msgOrOpts.toLowerCase().includes("löschen") });
      }
      return confirm(msgOrOpts || {});
    };
    return () => { try { delete window._domivusConfirm; } catch { /* ignore */ } };
  }, [confirm]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={state.open} onOpenChange={(o) => !o && close(false)}>
        <DialogContent className="max-w-md" data-testid="global-confirm-dialog">
          <DialogHeader>
            <div className="flex items-start gap-3">
              {state.opts.danger && (
                <div className="w-10 h-10 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center shrink-0">
                  <Warning size={20} className="text-rose-600" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <DialogTitle data-testid="confirm-title">{state.opts.title}</DialogTitle>
                {state.opts.description && (
                  <DialogDescription className="mt-1.5" data-testid="confirm-description">
                    {state.opts.description}
                  </DialogDescription>
                )}
              </div>
            </div>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => close(false)} data-testid="confirm-cancel">
              {state.opts.cancelLabel}
            </Button>
            <Button
              onClick={() => close(true)}
              className={state.opts.danger ? "bg-rose-600 hover:bg-rose-700" : "bg-slate-900 hover:bg-slate-800"}
              data-testid="confirm-ok"
            >
              {state.opts.confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback (in Storybook/Tests ohne Provider) → window.confirm
    return async (opts = {}) => await window._domivusConfirm(opts.title || "Sicher?");
  }
  return ctx;
}
